import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useDraftMode } from "../contexts/DraftModeContext";
import { useRequestsContext } from "../contexts/RequestsContext";
import { useSelfScheduling } from "../contexts/SelfScheduling/SelfSchedulingContext";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { trackEvent } from "../utils/analytics";
import { getWeekStart } from "../utils/dateUtils";
import type { ScheduleDayShift, ScheduleSlot } from "../utils/departmentWeeks";
import {
	type AgencyPostingFailure,
	ChangesSource,
	ChangeType,
	DEFAULT_WEEK_START_DAY,
	type NurseInShift,
	type NurseInShiftCreationSource,
	type NurseTrainingStatus,
	type PublishRequestBody,
	type ShiftChange,
} from "../utils/models";
import { useDepartmentFlags } from "./useDepartmentFlags";
import { useErrorSnackbar } from "./useErrorSnackbar";
import { useInvalidateCallIns } from "./useInvalidateCallIns";
import { usePublishChanges } from "./usePublishChanges";
import { useScheduleSaves } from "./useScheduleSaves";

/** One nurse of a multi-add: a prepared shift (`prepareDataForAddNurse` + charge/training/job) and the day and shift it joins. */
export type AddNursesEntry = ScheduleDayShift & {
	nurse: NurseInShift;
	location: string;
};

/** The hours a caller lays over the shift it saves. */
type ShiftHours = Pick<NurseInShift, "startTime" | "endTime">;

/** The other end of a move: where the staff member is moved to, and the hours there. */
type MoveTarget = { otherDate: DateTime; otherShiftType: string; otherShiftTimes: { start: DateTime; end: DateTime } };

export type AddNurseParams = ScheduleDayShift & {
	nurse: NurseInShift;
	location?: string;
	requestId?: string;
	isActual?: boolean;
	shiftTimes?: ShiftHours;
	source: NurseInShiftCreationSource;
	/** Save a past one-off correction as a published shift in departments that normally queue edits. */
	applyImmediately?: boolean;
};

type MoveNurseParams = ScheduleDayShift &
	MoveTarget & {
		nurse: NurseInShift;
		location?: string;
		requestId?: string;
		isActual?: boolean;
		shiftTimes?: ShiftHours;
	};

/** One existing shift, the day it's listed under, and which side of the schedule it's on. */
type ShiftOnDayParams = ScheduleDayShift & { nurse: NurseInShift; isActual?: boolean };

type DiscardMoveParams = ShiftOnDayParams & {
	otherNurse: NurseInShift;
	otherShiftTimes: { start: DateTime; end: DateTime };
	location?: string;
	shiftTimes?: ShiftHours;
};

type TrainingStatusParams = ScheduleDayShift & { shifts: NurseInShift[]; shiftsActual: NurseInShift[]; trainingStatus: NurseTrainingStatus };

export type AddNursesParams = {
	entries: AddNursesEntry[];
	/** Every entry lands in the same bucket: one publish writes either scheduled or actual changes. */
	isActual: boolean;
	source: NurseInShiftCreationSource;
};

/** The `/staffing/publish` payload fields callers act on. Draft mode never reaches the server, so it only sets `shouldRefresh`. */
export type AddNursesResult = {
	shouldRefresh: boolean;
	cancelledPto?: { nurseId: number; preferenceId: string }[];
	agency_posting_failures?: AgencyPostingFailure[];
};

/** The slot fields a publish change still carries. The server requires them but never reads them. */
type ChangeSlot = Pick<ShiftChange, "weekIndex" | "dayIndex" | "shiftIndex">;

export const toChangeSlot = (slot: ScheduleSlot | undefined): ChangeSlot => ({
	// DEFAULT: -1 for a day outside the loaded weeks (the profile's X-week grid adds there); the server ignores these.
	weekIndex: slot?.weekIndex ?? -1,
	dayIndex: slot?.dayIndex ?? -1,
	shiftIndex: slot?.shiftIndex ?? -1,
});

type InsertChangeArgs = {
	/** The prepared shift, with the caller's location and hours already laid over it. */
	nurse: NurseInShift;
	slot: ChangeSlot;
	date: DateTime;
	/** Auto-scheduler departments land adds pending. */
	isPending: boolean;
};

/** The publish change that inserts one shift — the one shape both `addNurse` and `addNurses` send. */
const buildInsertChange = ({ nurse, slot, date, isPending }: InsertChangeArgs): ShiftChange => ({
	nurse: { ...nurse, isPending },
	...slot,
	shiftType: nurse.shiftType,
	date,
	departmentId: nurse.departmentId,
	changeType: ChangeType.Insert,
});

type PublishBodyArgs = {
	changes: ShiftChange[];
	/** Every change lands in the same bucket: one publish writes either scheduled or actual changes. */
	isActual: boolean;
	source: NurseInShiftCreationSource;
	/** Request id → the nurse ids this add fills it with (`{}` when the add fills no request). */
	acceptedRequests: PublishRequestBody["accepted_requests"];
};

/** Files every change under one bucket: a single publish writes either scheduled or actual changes. */
const bucketChanges = (changes: ShiftChange[], isActual: boolean): Pick<PublishRequestBody, "changes" | "actual_changes"> => ({
	actual_changes: isActual ? changes : [],
	changes: isActual ? [] : changes,
});

/** The `/staffing/publish` body for a Staffing-page add. */
const buildPublishBody = ({
	changes,
	isActual,
	source,
	acceptedRequests,
}: PublishBodyArgs): PublishRequestBody & { creationSource: NurseInShiftCreationSource } => ({
	accepted_requests: acceptedRequests,
	declined_requests: {},
	...bucketChanges(changes, isActual),
	source: ChangesSource.Staffing,
	creationSource: source,
});

/** One shift a remove takes off the schedule, with the day and shift it's listed under. */
export type RemoveNursesEntry = ScheduleDayShift & {
	nurse: NurseInShift;
};

/** What the manager recorded about the removal. One set covers the whole batch: the footer asks once. */
export type RemovalDetails = {
	/** The full reason (`getFullReason()`); stored on the shift as the call-in message. */
	message?: string;
	/** The free-text note sent to the removed staff member. */
	removalMessage?: string;
	/** When the staff member called in — only for a call-in reason. */
	callInTime?: DateTime;
};

export type RemoveNursesParams = RemovalDetails & {
	entries: RemoveNursesEntry[];
	/** Every entry lands in the same bucket: one publish writes either scheduled or actual changes. */
	isActual: boolean;
	source: ChangesSource;
};

/** The `/staffing/publish` field callers act on. Draft mode never reaches the server, so it only sets `shouldRefresh`. */
export type RemoveNursesResult = { shouldRefresh: boolean };

/** A removal change carries the call-in time as the UTC ISO string the publish route stores, not a `DateTime`. */
type RemovalChange = Omit<ShiftChange, "nurse"> & { nurse: RemovedShift & { callInTime?: string | null } };

/**
 * The shift a removal sends. The publish route needs only its id, staff member, department and times and defaults the
 * rest, so a removal re-sent for a called-off shift (the profile's call-in edits) carries just the fields it has.
 */
export type RemovedShift = Pick<NurseInShift, "id" | "nurseId" | "departmentId" | "startTime" | "endTime" | "shiftType"> & Partial<NurseInShift>;

type RemovalChangeArgs = RemovalDetails & {
	nurse: RemovedShift;
	slot: ChangeSlot;
	date: DateTime;
	/** The shift's own type, else its Day/Night bucket's. */
	shiftType: string;
	/**
	 * Auto-scheduler departments and a self-scheduling import period keep the shift and mark it pending-delete
	 * (an `Update`) for the manager to publish; everywhere else the removal is a real `Delete`.
	 */
	isPendingRemoval: boolean;
};

/** The publish change that removes one shift — the one shape both `removeNurse` and `removeNurses` send. */
const buildRemovalChange = ({ nurse, slot, date, shiftType, isPendingRemoval, message, removalMessage, callInTime }: RemovalChangeArgs): RemovalChange => ({
	nurse: {
		...nurse,
		isPending: isPendingRemoval,
		isPendingToDelete: isPendingRemoval,
		message,
		removalMessage,
		callInTime: callInTime?.toUTC().toISO(),
	},
	...slot,
	shiftType,
	date,
	departmentId: nurse.departmentId,
	changeType: isPendingRemoval ? ChangeType.Update : ChangeType.Delete,
});

/** The `/staffing/publish` body for a removal — no request is accepted or declined by it. */
const buildRemovalPublishBody = ({
	changes,
	isActual,
	source,
}: {
	changes: RemovalChange[];
	isActual: boolean;
	source: ChangesSource;
}): PublishRequestBody => ({
	accepted_requests: {},
	declined_requests: {},
	// A removal's shift may carry only the fields the route needs (see `RemovedShift`); the route defaults the rest.
	...bucketChanges(changes as ShiftChange[], isActual),
	source,
});

export function useHandleNurseChange() {
	const { oldListSlotOf, getShiftScheduleDates, afterScheduleWrite, afterPublish } = useScheduleSaves();
	const { chosenDepartment } = useChosenDepartment();
	const { isAutoScheduler, isHospital } = useDepartmentFlags();
	const showErrorSnackbar = useErrorSnackbar();
	const { showSnackbar } = useSnackbar();
	const { refreshRequests } = useRequestsContext();
	const { isDraftMode } = useDraftMode();
	const { isDataInSelfSchedulingPeriod } = useSelfScheduling();
	const { mutateAsync: publishChanges } = usePublishChanges();
	const { track, trackError } = useAnalytics();
	const invalidateCallIns = useInvalidateCallIns();
	const { t } = useTranslation();

	const changeSlot = (date: DateTime, shiftType: string): ChangeSlot => toChangeSlot(oldListSlotOf(date, shiftType));

	/** Resolves the day-dependent parts of a removal (shift type, pending rule) the same way for every caller. */
	const toRemovalChange = (
		{ nurse, date, shiftType }: ScheduleDayShift & { nurse: RemovedShift },
		details: RemovalDetails,
		applyImmediately: boolean,
	): RemovalChange =>
		buildRemovalChange({
			nurse,
			...details,
			slot: changeSlot(date, shiftType),
			date,
			shiftType: nurse.shiftType ?? shiftType,
			// DEFAULT: ordinary changes follow the department's pending workflow; the past correction entry point opts into an immediate edit.
			isPendingRemoval: applyImmediately ? false : isAutoScheduler || isDataInSelfSchedulingPeriod(date),
		});

	/** A swap exchanges two loaded shifts; when either isn't loaded, its dates are unknown and every week reloads. */
	const afterSwap = (shouldRefresh: boolean, originShiftId: string, targetShiftId: string): Promise<void> => {
		const dates = getShiftScheduleDates([originShiftId, targetShiftId]);
		return dates === null ? afterScheduleWrite({ allWeeks: true }) : afterPublish(shouldRefresh, dates);
	};

	const {
		mutateAsync: addNurse,
		isPending: isAddingNurse,
		error: addNurseError,
	} = useMutation({
		mutationFn: async ({ nurse, date, shiftType, location, requestId, isActual, shiftTimes, source, applyImmediately }: AddNurseParams) => {
			if (applyImmediately && isDraftMode) throw new Error("Immediate schedule additions cannot be saved in draft mode");
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}

			const change = buildInsertChange({
				nurse: { ...nurse, location, ...shiftTimes },
				slot: changeSlot(date, shiftType),
				date,
				isPending: applyImmediately ? false : isAutoScheduler,
			});
			const updateRequestBody = buildPublishBody({
				changes: [change],
				isActual: !!isActual,
				source,
				acceptedRequests: requestId ? { [requestId]: [nurse.nurseId] } : {},
			});

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(response.shouldRefresh && !applyImmediately, [date]);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const {
		mutateAsync: addNurses,
		isPending: isAddingNurses,
		error: addNursesError,
	} = useMutation({
		mutationFn: async ({ entries, isActual, source }: AddNursesParams): Promise<AddNursesResult> => {
			// Draft mode: no backend call — auto-tracking picks up the local adds, exactly like addNurse.
			if (isDraftMode) {
				return { shouldRefresh: false };
			}
			if (entries.length === 0) {
				throw new Error("addNurses called without any nurses to add");
			}

			// One Insert per nurse, built exactly like `addNurse`'s, so N adds commit in ONE publish transaction:
			// all-or-nothing on the backend, which is why callers must exclude hard conflicts up front.
			const changes = entries.map(({ nurse, date, shiftType, location }) =>
				buildInsertChange({
					nurse: { ...nurse, location },
					slot: changeSlot(date, shiftType),
					date,
					isPending: isAutoScheduler,
				}),
			);
			const updateRequestBody = buildPublishBody({ changes, isActual, source, acceptedRequests: {} });

			const response: AddNursesResult = await publishChanges(updateRequestBody);
			void afterPublish(
				response.shouldRefresh,
				entries.map((entry) => entry.date),
			);
			return response;
		},
	});

	const {
		mutateAsync: moveNurse,
		isPending: isMovingNurse,
		error: moveNurseError,
	} = useMutation({
		mutationFn: async ({ nurse, date, shiftType, otherDate, otherShiftType, location, requestId, isActual, shiftTimes, otherShiftTimes }: MoveNurseParams) => {
			const newNurseId = v4();
			const slot = changeSlot(date, shiftType);
			const otherSlot = changeSlot(otherDate, otherShiftType);

			const changes = [
				{
					nurse: {
						...nurse,
						isPending: isHospital,
						isPendingToDelete: isHospital,
						originalDay: otherShiftTimes.start.weekday % 7,
						originalShift: slot.shiftIndex,
						...shiftTimes,
					},
					...slot,
					shiftType: nurse.shiftType ?? shiftType,
					date,
					departmentId: nurse.departmentId,
					changeType: ChangeType.Update,
				},
				{
					nurse: {
						...nurse,
						id: newNurseId,
						isPending: isHospital,
						location,
						startTime: otherShiftTimes.start,
						endTime: otherShiftTimes.end,
						originalDay: slot.dayIndex,
						originalShift: slot.shiftIndex,
						isPendingToDelete: false,
					},
					...otherSlot,
					shiftType: nurse.shiftType,
					date: otherDate,
					departmentId: nurse.departmentId,
					changeType: ChangeType.Insert,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (requestId) {
				updateRequestBody.accepted_requests = {
					[requestId]: [nurse.nurseId],
				};
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(response.shouldRefresh, [date, otherDate]);
			}

			return newNurseId;
		},
	});

	const {
		mutateAsync: swapNurses,
		isPending: isSwappingNurses,
		error: swapNursesError,
	} = useMutation({
		mutationFn: async ({ originShiftId, targetShiftId }: { originShiftId: string; targetShiftId: string }) => {
			const response = await backendService.createAdminSwapShiftRequest(chosenDepartment.department_id, {
				originShiftId,
				targetShiftId,
			});
			void afterSwap(response.data.shouldRefresh === true, originShiftId, targetShiftId);
			return response.data;
		},
		onSuccess: (_data, variables) => {
			track("admin_swap_submitted", {
				origin_shift_id: variables.originShiftId,
				target_shift_id: variables.targetShiftId,
				department_id: chosenDepartment.department_id,
			});
		},
		onError: (error) => {
			let message = "Swap nurses failed";
			if (error instanceof AxiosError) {
				const detail = error.response?.data?.detail;
				message = (typeof detail === "string" ? detail : detail?.message) || message;
			}
			showErrorSnackbar(message, {}, error);
		},
	});

	const {
		mutateAsync: swapBetweenShifts,
		isPending: isSwappingBetweenShifts,
		error: swapBetweenShiftsError,
	} = useMutation({
		mutationFn: async ({ originShiftId, targetShiftId }: { originShiftId: string; targetShiftId: string }) => {
			const response = await backendService.createSwapBetweenRequest(chosenDepartment.department_id, {
				originShiftId,
				targetShiftId,
			});
			void afterSwap(response.data.shouldRefresh === true, originShiftId, targetShiftId);
			return response.data;
		},
		onSuccess: async (_data, variables) => {
			track("bedside_swap_requested", {
				origin_shift_id: variables.originShiftId,
				target_shift_id: variables.targetShiftId,
				department_id: chosenDepartment.department_id,
			});
			showSnackbar("Swap request created successfully", "success");
			await refreshRequests();
		},
		onError: (error) => {
			let message = "Swap between shifts failed";
			if (error instanceof AxiosError) {
				const detail = error.response?.data?.detail;
				message = (typeof detail === "string" ? detail : detail?.message) || message;
			}
			showErrorSnackbar(message, {}, error);
		},
	});

	const {
		mutateAsync: updateNurse,
		isPending: isUpdatingNurse,
		error: updateNurseError,
	} = useMutation({
		mutationFn: async ({ nurse, date, shiftType, isActual }: ScheduleDayShift & { nurse: Partial<NurseInShift>; isActual?: boolean }) => {
			const slot = changeSlot(date, shiftType);
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}

			const changes = [
				{
					nurse: {
						...nurse,
						isPending: isHospital,
						isPendingToDelete: isHospital,
					} as NurseInShift,
					...slot,
					shiftType,
					date,
					departmentId: nurse.departmentId as string,
					changeType: ChangeType.Update,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: isActual ? changes : [],
				changes: isActual ? [] : changes,
				source: ChangesSource.Staffing,
			};

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(response.shouldRefresh, [date]);
				trackEvent("update_nurse_in_shift", { nurse, shiftIndex: slot.shiftIndex, dayNo: slot.dayIndex, weekIndex: slot.weekIndex });
				return response;
			}
			trackEvent("update_nurse_in_shift", { nurse, shiftIndex: slot.shiftIndex, dayNo: slot.dayIndex, weekIndex: slot.weekIndex });
			return { shouldRefresh: false };
		},
		onError: (error, variables) => {
			// A translated fallback; showErrorSnackbar still replaces it with the server's own translated reason
			// (a UIVisibleError, an overlap conflict) whenever the error carries one.
			if (error instanceof AxiosError) {
				showErrorSnackbar(t("error.update.nurse.shift"), { nurseId: variables.nurse?.id }, error);
			} else {
				showErrorSnackbar(t("error.update.nurse.shift"), {
					nurseId: variables.nurse?.id,
				});
			}
		},
	});

	const {
		mutateAsync: discardNurseMove,
		isPending: isDiscarding,
		error: discardNurseError,
	} = useMutation({
		mutationFn: async ({ nurse, date, shiftType, location, isActual, shiftTimes, otherShiftTimes, otherNurse }: DiscardMoveParams) => {
			let changes: any[] = [];
			// A pending move records its other end by day number in the same week.
			const { weekIndex, shiftIndex, dayIndex: dayNo } = changeSlot(date, shiftType);
			const otherDayNo = (nurse.isPendingToDelete ? otherNurse : nurse).originalDay;
			if (otherDayNo === undefined) throw new Error(`Pending move ${nurse.id} has no other day to discard to`);
			// The other end is that day of the same Sunday week, loaded or not.
			const otherDate = getWeekStart(date, DEFAULT_WEEK_START_DAY).plus({ days: otherDayNo });

			if (nurse.isPendingToDelete) {
				changes = [
					{
						nurse: {
							...nurse,
							isPending: false,
							isPendingToDelete: false,
							isPendingToMove: false,
							isPublished: true,
						},
						shiftIndex,
						shiftType,
						weekIndex,
						dayIndex: dayNo,
						date,
						departmentId: nurse.departmentId,
						changeType: ChangeType.Update,
					},
					{
						nurse: {
							...otherNurse,
							isPending: false,
							isPendingToDelete: false,
							isPublished: false,
							isPendingToMove: false,
							location,
							originalDay: dayNo,
							originalShift: shiftIndex,
							...shiftTimes,
						},
						shiftIndex,
						shiftType,
						weekIndex,
						dayIndex: dayNo,
						date: otherDate,
						departmentId: nurse.departmentId,
						changeType: ChangeType.Delete,
					},
				];
			} else {
				changes = [
					{
						nurse: {
							...nurse,
							isPending: false,
							isPendingToDelete: false,
							isPublished: false,
							isPendingToMove: false,
							location,
							originalDay: dayNo,
							originalShift: shiftIndex,
							...shiftTimes,
						},
						shiftIndex,
						shiftType,
						weekIndex,
						dayIndex: dayNo,
						date,
						departmentId: nurse.departmentId,
						changeType: ChangeType.Delete,
					},
					{
						nurse: {
							...otherNurse,
							isPending: false,
							isPendingToDelete: false,
							isPendingToMove: false,
							isPublished: true,
							location,
							originalDay: dayNo,
							originalShift: shiftIndex,
							startTime: otherShiftTimes.start,
							endTime: otherShiftTimes.end,
						},
						shiftIndex,
						shiftType,
						weekIndex,
						dayIndex: otherDayNo,
						date: otherDate,
						departmentId: nurse.departmentId,
						changeType: ChangeType.Update,
					},
				];
			}

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(
					response.shouldRefresh,
					changes.map((change) => change.date),
				);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const { mutateAsync: discardNurseRemove } = useMutation({
		mutationFn: async ({ nurse, date, shiftType, isActual }: ShiftOnDayParams) => {
			const isImportingSelfScheduling = isDataInSelfSchedulingPeriod(date);
			const changes: any[] = [
				{
					nurse: {
						...nurse,
						isPendingToDelete: false,
						isPending: isImportingSelfScheduling,
					},
					...changeSlot(date, shiftType),
					shiftType,
					date,
					departmentId: nurse.departmentId,
					changeType: ChangeType.Update,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(response.shouldRefresh, [date]);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const { mutateAsync: discardNurseAdd } = useMutation({
		mutationFn: async ({ nurse, date, shiftType, isActual }: ShiftOnDayParams) => {
			const isImportingSelfScheduling = isDataInSelfSchedulingPeriod(date);
			const changes: any[] = [
				{
					nurse: {
						...nurse,
						isPendingToDelete: isImportingSelfScheduling,
					},
					...changeSlot(date, shiftType),
					shiftType,
					date,
					departmentId: nurse.departmentId,
					changeType: isImportingSelfScheduling ? ChangeType.Update : ChangeType.Delete,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(response.shouldRefresh, [date]);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const {
		mutateAsync: removeNurse,
		isPending: isRemovingNurse,
		error: removeNurseError,
	} = useMutation({
		mutationFn: async ({
			nurse,
			date,
			shiftType,
			isActual,
			message,
			removalMessage,
			source,
			callInTime,
			applyImmediately,
		}: ScheduleDayShift & RemovalDetails & { nurse: RemovedShift; isActual?: boolean; source?: ChangesSource; applyImmediately?: boolean }) => {
			if (applyImmediately && isDraftMode) throw new Error("Immediate schedule removals cannot be saved in draft mode");
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}

			const change = toRemovalChange({ nurse, date, shiftType }, { message, removalMessage, callInTime }, applyImmediately === true);
			const updateRequestBody = buildRemovalPublishBody({
				changes: [change],
				isActual: !!isActual,
				// DEFAULT: the single remove predates the explicit source; every Staffing-page caller omits it.
				source: source ?? ChangesSource.Staffing,
			});

			const response = await publishChanges(updateRequestBody);
			void afterPublish(response.shouldRefresh && !applyImmediately, [date]);
			return response;
		},
		// A removal is a call-in write: it is what puts a row in the call-ins list and a marker on the
		// profile calendar. Nothing else in this flow touches that cache, so without this the manager
		// who just did the removal is the one person who cannot see it. Draft mode made no backend
		// change, so there is nothing to re-read.
		onSuccess: () => {
			if (isDraftMode) return;
			void invalidateCallIns();
		},
	});

	/**
	 * Removes N shifts in ONE publish — each change built exactly like `removeNurse`'s, so the batch is one
	 * backend transaction: all-or-nothing. The caller owns the snackbar (it knows who was removed), as with
	 * `removeNurse`; a rejected publish rejects this promise.
	 */
	const {
		mutateAsync: removeNurses,
		isPending: isRemovingNurses,
		error: removeNursesError,
	} = useMutation({
		mutationFn: async ({ entries, isActual, source, message, removalMessage, callInTime }: RemoveNursesParams): Promise<RemoveNursesResult> => {
			// Draft mode: no backend call — auto-tracking picks up the local removals, exactly like removeNurse.
			if (isDraftMode) {
				return { shouldRefresh: false };
			}
			if (entries.length === 0) {
				throw new Error("removeNurses called without any shifts to remove");
			}

			const changes = entries.map((entry) => toRemovalChange(entry, { message, removalMessage, callInTime }, false));
			const response: RemoveNursesResult = await publishChanges(buildRemovalPublishBody({ changes, isActual, source }));
			void afterPublish(
				response.shouldRefresh,
				entries.map((entry) => entry.date),
			);
			return response;
		},
		// Same call-in cache as `removeNurse`: the removals are call-in writes.
		onSuccess: () => {
			if (isDraftMode) return;
			void invalidateCallIns();
		},
		// Recorded here so no failed batch goes untracked; the caller still owns the snackbar (it knows the names).
		onError: (error, { entries, isActual, source }) => {
			trackError("remove_nurses_error", {
				department_id: chosenDepartment.department_id,
				shift_ids: entries.map((entry) => entry.nurse.id).join(","),
				shift_count: entries.length,
				is_actual: isActual,
				source,
				error_message: error instanceof Error ? error.message : String(error),
			});
		},
	});

	const {
		mutateAsync: toggleNurseCharge,
		isPending: isTogglingNurseCharge,
		error: nurseChargeError,
	} = useMutation({
		mutationFn: async ({ nurse, date, shiftType, isActual }: ShiftOnDayParams) => {
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}

			const changes = [
				{
					nurse: {
						...nurse,
						charge: !nurse.charge,
					},
					...changeSlot(date, shiftType),
					shiftType: nurse.shiftType,
					date,
					departmentId: nurse.departmentId,
					changeType: ChangeType.Update,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(response.shouldRefresh, [date]);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const {
		mutateAsync: setNurseTrainingStatus,
		isPending: isLoadingTrainingStatus,
		error: trainingStatusError,
	} = useMutation({
		mutationFn: async ({ shifts, shiftsActual, date, shiftType, trainingStatus }: TrainingStatusParams) => {
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}
			const isImportingSelfScheduling = isDataInSelfSchedulingPeriod(date);
			const slot = changeSlot(date, shiftType);

			const changes = shifts.map((nurse: NurseInShift) => ({
				nurse: {
					...nurse,
					trainingStatus,
					isPending: isImportingSelfScheduling,
				},
				...slot,
				shiftType: nurse.shiftType,
				date,
				departmentId: nurse.departmentId,
				changeType: ChangeType.Update,
			}));

			const changesActual = shiftsActual.map((nurse: NurseInShift) => ({
				nurse: {
					...nurse,
					trainingStatus,
					isPending: false,
				},
				...slot,
				shiftType: nurse.shiftType,
				date,
				departmentId: nurse.departmentId,
				changeType: ChangeType.Update,
			}));

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: changesActual,
				changes: changes,
				source: ChangesSource.Staffing,
			};

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				void afterPublish(response.shouldRefresh, [date]);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	return {
		addNurse,
		addNurses,
		removeNurse,
		removeNurses,
		toggleNurseCharge,
		discardNurseAdd,
		setNurseTrainingStatus,
		moveNurse,
		swapNurses,
		swapBetweenShifts,
		discardNurseMove,
		discardNurseRemove,
		updateNurse,
		isLoading: {
			addNurse: isAddingNurse,
			addNurses: isAddingNurses,
			removeNurse: isRemovingNurse,
			removeNurses: isRemovingNurses,
			toggleNurseCharge: isTogglingNurseCharge,
			setNurseTrainingStatus: isLoadingTrainingStatus,
			moveNurse: isMovingNurse,
			swapNurses: isSwappingNurses,
			swapBetweenShifts: isSwappingBetweenShifts,
			discardNurseMove: isDiscarding,
			updateNurse: isUpdatingNurse,
		},
		error: {
			addNurse: addNurseError,
			addNurses: addNursesError,
			removeNurse: removeNurseError,
			removeNurses: removeNursesError,
			toggleNurseCharge: nurseChargeError,
			setNurseTrainingStatus: trainingStatusError,
			moveNurse: moveNurseError,
			swapNurses: swapNursesError,
			swapBetweenShifts: swapBetweenShiftsError,
			discardNurseMove: discardNurseError,
			updateNurse: updateNurseError,
		},
	};
}
